import axios from 'axios';
import FetchRequestList from './FetchRequestList';

import store from '../store';
import storage, { keys } from './storage';
import { appActions } from '../store/modules/app';
import { asyncSimpleModal } from './common';
import { COMMON_RES_CODE } from '../constants/responseCode/commonResCode';
import { ERROR_RESPONSE } from '../constants/errorResponse';
import { EXCEL_UPLOAD_ERROR_CODE } from '../constants/errorResponse/category/excelUpload';
import { COMMON_ERROR_CODE } from '../constants/errorResponse/common';
import { HTTP_METHODS } from '../constants/service/common/utilConstants';

const getBaseUrl = () => {
  if (import.meta.env?.VITE_API_URL_NESTJS) {
    return import.meta.env.VITE_API_URL_NESTJS;
  }
  return 'https://www.testApiV2.com';
};

const SuperFetchV2 = axios.create({
  baseURL: getBaseUrl() + '/api',
});

const etagMap = new Map();

const customHeader = () => {
  const user = storage.get(keys.user);
  return {
    accept: 'application/json',
    authorization: user ? 'Bearer ' + user : undefined,
  };
};

/**
 *  서버에 request 를 보내기 전에
 *  전체 스피너 작동여부를 판담함
 *
 *  요청 config 에 아래 플래그를 실으면 공통 동작을 건너뛴다.
 *  - skipFullscreenLoader: 전역 풀스크린 로더 미사용 (폴링처럼 주기적으로 나가는 요청이
 *    어드민 전체를 블로킹하는 것을 막기 위함). FetchRequestList add/complete 를 함께
 *    건너뛰어야 in-flight 카운터가 어긋나지 않는다.
 *  - skipErrorModal: 에러 코드 매핑 모달 미노출 (호출부가 토스트 등으로 직접 처리)
 */
SuperFetchV2.interceptors.request.use((req) => {
  const url = req.url;

  // 기본 헤더 설정
  req.headers = customHeader();

  if ((req.method === HTTP_METHODS.PUT || req.method === HTTP_METHODS.POST) && etagMap.has(url)) {
    req.headers['If-Match'] = etagMap.get(url);
  }

  if (!req.skipFullscreenLoader) {
    store.dispatch({ type: appActions.SET_FULLSCREEN_LOADER, payload: true });
    FetchRequestList.getInstance().add(req.url);
  }

  return req;
});

/**
 *  서버에서 response 을 받으면,
 *  전체 스피너를 종료함
 *  서버의 응답 코드에 따른 분기처리함
 */
SuperFetchV2.interceptors.response.use(
  async (res) => {
    const url = res.config.url;
    const etag = res.headers['etag'];
    if (etag) {
      etagMap.set(url, etag);
    } else {
      etagMap.delete(url);
    }

    if (!res.config.skipFullscreenLoader) {
      const fetchRequestList = FetchRequestList.getInstance();
      fetchRequestList.complete(res.config.url.replace(res.config.baseURL, ''));

      /**
       *  모든 요청이 응답 완료 시 전체 스피너 종료
       */
      if (fetchRequestList.isAllComplete) {
        store.dispatch({ type: appActions.SET_FULLSCREEN_LOADER, payload: false });
      }
    }

    return res.data;
  },
  async (error) => {
    /**
     *  로더를 쓰지 않는 요청(폴링 등)의 실패는 로더 상태를 건드리지 않는다.
     *  같은 시점에 진행 중인 다른 요청의 로더를 꺼버리는 것을 막기 위함.
     */
    if (!error.config?.skipFullscreenLoader) {
      const fetchRequestList = FetchRequestList.getInstance();
      // 스피너 토글 비노출 처리
      store.dispatch({ type: appActions.SET_FULLSCREEN_LOADER, payload: false });
      // 요청한 api 배열 초기화
      fetchRequestList.reset();
    }

    if (error?.response?.data) {
      const { status, errors } = error.response.data;
      const errMsg = errors?.map((error) => ERROR_RESPONSE[error?.code]).join('\n');

      // 비활성화된 관리자가 api 요청 시 에러 모달 노출
      if (status === COMMON_RES_CODE.INACTIVE_USER_INVALID_REQUEST && errMsg === COMMON_ERROR_CODE['COMMON_007']) {
        const proceed = await asyncSimpleModal({
          type: 'error',
          title: '비활성 관리자',
          message: '관리자 계정이 비활성화되어 로그아웃으로 전환됩니다.',
        });

        if (proceed) {
          /**
           *  토큰 삭제
           */
          storage.remove(keys.user);
          window.location.reload();
        }
      }

      if (status === COMMON_RES_CODE.EXPIRATION_TOKEN_USER_REQUEST) {
        const proceed = await asyncSimpleModal({
          type: 'error',
          title: '만료된 토큰',
          message: '만료된 토큰입니다. 로그아웃으로 전환됩니다.',
        });

        if (proceed) {
          /**
           *  토큰 삭제
           */
          storage.remove(keys.user);
          window.location.reload();
        }
      }

      // 에러 코드가 'EXCELUPLOAD002' 일 때, 에러 모달 비노출 처리(임시)
      // skipErrorModal 요청은 호출부가 직접 메시지를 처리하므로 모달을 띄우지 않는다.
      if (errMsg && !error.config?.skipErrorModal && errors?.[0]?.code !== EXCEL_UPLOAD_ERROR_CODE.EXCELUPLOAD_002) {
        await asyncSimpleModal({
          type: 'error',
          title: status,
          message: errMsg,
        });
      }
    }

    return Promise.reject(error);
  },
);

SuperFetchV2.defaults.timeout = 120000;

export default SuperFetchV2;
